// components/capture/WriteRegion.jsx — Anamoria SPA
// v1.1 — 2026-08-30 (DF-7 PR2): initial writeExpanded opens when text is seeded
//   (edit view), stays compact when empty (create). Initial-state/presentation only.
// v1.0 — C1 extraction from AddMemoryPage v0.3 (July 22, 2026)
//
// Pure relocation (B-2(a), momentedit-4-1-spa-DRAFT-for-review-v0.4.md C1):
// the Write capture region — compact-idle / expand-in-place (R7), first-words
// chip (R8), copy v0.2 §3 strings — JSX and mechanics byte-identical to the
// v0.3 in-page block. Expansion state + textarea ref were region-internal in
// effect (nothing else read them) and are now internal in fact.
// Styles intentionally remain in AddMemoryPage.module.css (single source of
// visual truth; recorded in draft v0.4 — a CSS relocation is a separate,
// later change if ever wanted).
//
// v1.1 — C2: optional hideRemove prop (A-6 — the sole remaining piece keeps
//   its Remove hidden; in-place edit surface). Default false: create-mode
//   behaviour byte-identical.
//
// v1.2 — 2.2b: AD card reframe (July 29, 2026). Idle state wrapped in the shared
//   RegionCard (collapsed entry → expand-in-place); textarea/chip behaviour and
//   the R8 first-words chip unchanged. Visible label moved into the card head
//   (legend now hidden); helper WRITE_HELPER — copy pending v0.3 ratification.
//
// Props:
//   text       — string — the single written piece (parent-owned)
//   onChange   — function(next) — replaces the parent's setText
//   atCap      — boolean — MAX_PIECES reached (disables the empty trigger)
//   hideRemove — boolean — A-6 sole-piece rule (edit surfaces only)
//   className  — string — page entrance-animation class (parent-owned)

import { useState, useRef } from 'react';
import styles from '../../pages/AddMemoryPage.module.css';
import RegionCard, { WriteIcon } from './RegionCard';

// 2.2b helper line (AD mockup; copy pending v0.3 ratification).
const WRITE_HELPER = 'Write what you remember. Anything, or nothing at all.';

export default function WriteRegion({ text, onChange, atCap, hideRemove = false, className = '' }) {
  const [writeExpanded, setWriteExpanded] = useState(() => text.trim() !== ''); // open when seeded (edit); compact-idle when empty (create) — R7
  const textareaRef = useRef(null);

  const hasText = text.trim() !== '';
  const open = writeExpanded || hasText;                     // 2.2b: card stays open with content
  const expand = () => { setWriteExpanded(true); setTimeout(() => textareaRef.current?.focus(), 50); };

  return (
    <fieldset className={`${styles.region} ${className}`}>
      <legend className={styles.hiddenLabel}>Write</legend>
      <RegionCard
        icon={<WriteIcon />}
        title="Write"
        helper={WRITE_HELPER}
        open={open}
        disabled={atCap && !open}
        onToggle={expand}
      >
        {/* Expanded editor, or the collapsed-with-text first-words chip (R8) —
            behaviour byte-identical to v1.1, just inside the card body. */}
        {writeExpanded ? (
          <div className={styles.filledRegion}>
            <textarea
              ref={textareaRef}
              className={styles.writeArea}
              value={text}
              onChange={(e) => onChange(e.target.value)}
              onBlur={() => { if (!text.trim()) setWriteExpanded(false); }}
              aria-label="Write your memory"
              rows={5}
            />
            {hasText && !hideRemove && (
              <button
                type="button"
                className={styles.removeLink}
                onClick={() => { onChange(''); setWriteExpanded(false); }}
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <div className={styles.chipRow}>
            <button
              type="button"
              className={styles.chip}
              onClick={expand}
              aria-label="Edit your written memory"
            >
              <span className={styles.chipText}>{text.trim().slice(0, 60)}{text.trim().length > 60 ? '…' : ''}</span>
            </button>
            {!hideRemove && (
              <button type="button" className={styles.removeLink} onClick={() => onChange('')}>Remove</button>
            )}
          </div>
        )}
      </RegionCard>
    </fieldset>
  );
}
